import { useEffect, useState } from 'react';
import {
  Alert as MuiAlert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Campaign } from '@mui/icons-material';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as admin from '@/api/admin';
import type { BannerSeverity } from '@/api/admin';
import { toApiError } from '@/api/client';
import { LoadingScreen } from '@/components/LoadingScreen';
import { formatDateTime } from '@/lib/formatters';

export const Route = createFileRoute('/_authenticated/admin/anuncios/')({
  component: AnunciosPage,
});

const SEVERITY: Record<BannerSeverity, { label: string; preview: 'info' | 'warning' | 'error' }> = {
  Info: { label: 'Informativo', preview: 'info' },
  Warning: { label: 'Advertencia', preview: 'warning' },
  Critical: { label: 'Crítico', preview: 'error' },
};

type Toast = { message: string; severity: 'success' | 'error' };

function AnunciosPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<Toast | null>(null);

  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<BannerSeverity>('Info');
  const [active, setActive] = useState(false);

  const bannerQ = useQuery({ queryKey: ['admin-banner'], queryFn: admin.getBanner });

  // Hidrata el form con el banner existente una vez que llega.
  useEffect(() => {
    const b = bannerQ.data;
    if (b) {
      setMessage(b.message ?? '');
      setSeverity(b.severity);
      setActive(b.active);
    }
  }, [bannerQ.data]);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['admin-banner'] });
    void queryClient.invalidateQueries({ queryKey: ['platform-banner-active'] });
  };
  const onError = (err: unknown): void => setToast({ message: toApiError(err).message, severity: 'error' });

  const saveMut = useMutation({
    mutationFn: () => admin.setBanner({ message: message.trim(), severity, active }),
    onSuccess: () => {
      invalidate();
      setToast({ message: 'Banner guardado.', severity: 'success' });
    },
    onError,
  });

  const clearMut = useMutation({
    mutationFn: () => admin.clearBanner(),
    onSuccess: () => {
      setMessage('');
      setSeverity('Info');
      setActive(false);
      invalidate();
      setToast({ message: 'Banner borrado.', severity: 'success' });
    },
    onError,
  });

  if (bannerQ.isLoading) return <LoadingScreen message="Cargando anuncio…" />;

  const valid = message.trim() !== '';
  const pending = saveMut.isPending || clearMut.isPending;
  const updatedBy = bannerQ.data?.updatedBy;
  const updatedAt = bannerQ.data?.updatedAt;

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Campaign color="primary" /> Anuncios
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Banner global de mantenimiento / aviso. Se muestra a todos los usuarios autenticados del portal.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          <TextField
            label="Mensaje"
            fullWidth
            multiline
            minRows={2}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Ej: El sábado 28/6 de 2 a 4 AM habrá una ventana de mantenimiento."
          />
          <TextField
            select
            label="Severidad"
            fullWidth
            value={severity}
            onChange={(e) => setSeverity(e.target.value as BannerSeverity)}
          >
            {(Object.keys(SEVERITY) as BannerSeverity[]).map((s) => (
              <MenuItem key={s} value={s}>
                {SEVERITY[s].label}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
            label="Activo (visible para todos los usuarios)"
          />

          {valid && (
            <Box>
              <Typography variant="subtitle2" gutterBottom color="text.secondary">
                Vista previa
              </Typography>
              <MuiAlert severity={SEVERITY[severity].preview} sx={{ borderRadius: 1 }}>
                {message.trim()}
              </MuiAlert>
            </Box>
          )}

          {updatedAt && (
            <Typography variant="caption" color="text.secondary">
              Última actualización: {formatDateTime(updatedAt)}
              {updatedBy ? ` · por ${updatedBy}` : ''}
            </Typography>
          )}

          <Divider />

          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button color="error" disabled={pending} onClick={() => clearMut.mutate()}>
              {clearMut.isPending ? <CircularProgress size={18} /> : 'Borrar banner'}
            </Button>
            <Button variant="contained" disabled={!valid || pending} onClick={() => saveMut.mutate()}>
              {saveMut.isPending ? <CircularProgress size={18} /> : 'Guardar'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)} variant="filled">
          {toast?.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
